import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faFileSignature } from "@fortawesome/free-solid-svg-icons";
import "../css/DocumentsPage.css";

const API_URL = process.env.REACT_APP_API_URL;
const PENDING_SIGN_KEY = "esign_pending_sign_doc_id";
const LEGALESIGN_PDF_URL = "https://eu-api.legalesign.com/api/v1/pdf";
const LEGALESIGN_COMPLETE_URL = "https://app1.legalesign.com/esign/complete";
const UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** Extract signer UUID from Legalesign signing URL (path contains signer id). */
function getSignerIdFromSigningUrl(signingUrl) {
  if (!signingUrl || typeof signingUrl !== "string") return null;
  const match = signingUrl.match(UUID_REGEX);
  return match ? match[0] : null;
}
const DocumentsPage = ({ personId: propPersonId }) => {
  const personId = propPersonId || localStorage.getItem("personId");

  // FIX 1: Initialize from localStorage to prevent the "false -> true" toggle on load
  const [isMinor, setIsMinor] = useState(() => {
    const saved = localStorage.getItem("minorStatus");
    return saved === "Yes" || saved === "yes" || saved === "true";
  });

  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [participantId, setParticipantId] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false); // Track if we've checked the API

  const documentTitle = isMinor
    ? "IGF Registration Regulations (Minor)"
    : "IGF Registration Regulations (Athlete)";

    const fetchDocument = useCallback(async () => {
      if (!personId) return;
      try {
        const res = await axios.get(`${API_URL}/esign-documents/athlete/${personId}`);
        const dataArray = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);

        if (dataArray.length > 0) {
          const found =
            dataArray.find(doc => doc.title === "IGF Registration Regulations" ||
              (doc.title && doc.title.includes("IGF Registration Regulations"))) ||
            dataArray[0];
          setDocument(found || null);
        } else {
          setDocument(null);
        }
      } catch (err) {
        console.error("Failed to fetch document:", err);
        setDocument(null);
      }
    }, [personId]);

  // Combined Effect to load all initial data
  useEffect(() => {
    const init = async () => {
      if (!personId) return;
      
      try {
        // 1. Fetch Participant Data first to set Minor Status correctly
        const res = await axios.get(`${API_URL}/ems/participants`, { params: { slim: true } });
        const userParticipant = (res.data || []).find(
          record => record.person_id && String(record.person_id._id || record.person_id) === personId
        );

        if (userParticipant) {
          setParticipantId(userParticipant._id);
          const mStatus = userParticipant?.person_data?.minorStatus;
          const isM = mStatus === "Yes" || mStatus === "yes" || mStatus === true;
          
          setIsMinor(isM);
          localStorage.setItem("minorStatus", isM ? "Yes" : "No");
        }

        // 2. Then fetch the document
        await fetchDocument();
      } catch (e) {
        console.error("Init error", e);
      } finally {
        setIsDataLoaded(true); // Now we are ready to show the UI
      }
    };

    init();
  }, [personId, fetchDocument]);

  // When user returns from signing tab: call webhook so DB is updated to SIGNED (Legalesign can't reach localhost)
  useEffect(() => {
    const onVisibility = async () => {
      if (typeof window === "undefined" || window.document.visibilityState !== "visible" || !personId) return;
      const pendingDocId = sessionStorage.getItem(PENDING_SIGN_KEY);
      if (pendingDocId) {
        sessionStorage.removeItem(PENDING_SIGN_KEY);
        const cleanId = String(pendingDocId).replace(/^@+/, "").trim();
        if (cleanId) {
          await new Promise((r) => setTimeout(r, 2500));
          try {
            await axios.post("https://erp.igfdigital.com/api/esign-documents/webhook/legalesign", {
              data: { uuid: cleanId },
              event: "completed"
            });
          } catch (e) {
            console.warn("Webhook (update status):", e?.response?.data?.error || e?.message);
          }
        }
      }
      fetchDocument();
    };
    window.document.addEventListener("visibilitychange", onVisibility);
    return () => window.document.removeEventListener("visibilitychange", onVisibility);
  }, [personId, fetchDocument]);

  /**
   * Fetch participantId and minorStatus from API using personId
   */
  useEffect(() => {
    const fetchParticipantData = async () => {
      if (!personId) return;

      try {
        // Fetch all participants and find the one matching personId
        const res = await axios.get(`${API_URL}/ems/participants`, { params: { slim: true } });
        const allParticipants = res.data || [];
        const userParticipant = allParticipants.find(
          record => record.person_id && String(record.person_id._id || record.person_id) === personId
        );

        if (userParticipant?._id) {
          setParticipantId(userParticipant._id);
          
          // Check minorStatus from participant's person_data
          const minorStatus = userParticipant?.person_data?.minorStatus;
          const isMinorValue = minorStatus === "Yes" || minorStatus === "yes" || minorStatus === true;
          setIsMinor(isMinorValue);
          
          // Also update localStorage for consistency
          if (minorStatus) {
            localStorage.setItem("minorStatus", minorStatus === true ? "Yes" : String(minorStatus));
          }
          
          console.log("Participant minorStatus:", minorStatus, "isMinor:", isMinorValue);
        } else {
          console.warn("Participant not found for personId:", personId);
        }
      } catch (err) {
        console.error("Failed to fetch participant data:", err);
      }
    };

    fetchParticipantData();
  }, [personId, API_URL]);

  /**
   * Sign or download: use saved signingUrl/downloadUrl from DB. Only call generate when no document exists.
   */
  const handleSignOrDownload = async () => {
    if (!participantId) return alert("Participant ID not found.");
  
    // 1. If already SIGNED in DB, open the Legalesign complete page using signer ID
    if (document?.status === "SIGNED") {
      const signerId = getSignerIdFromSigningUrl(document.signingUrl) ||
        (document.legalesignDocId || "").replace(/^@+/, "").trim();
      if (signerId) return window.open(`${LEGALESIGN_COMPLETE_URL}/${signerId}/?r=1`, "_blank");
    }
  
    // 2. ONLY GENERATE ONE DOCUMENT: If we already have a signingUrl, use it!
    if (document?.signingUrl) {
      const docId = (document.legalesignDocId || "").replace(/^@+/, "").trim();
      sessionStorage.setItem(PENDING_SIGN_KEY, docId);
      return window.open(document.signingUrl, "_blank");
    }
  
    // 3. Only if NO document exists at all, call generate
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/esign-documents/generate`, {
        participantId: participantId,
      });
      if (response.data.signingUrl) {
        sessionStorage.setItem(PENDING_SIGN_KEY, response.data.documentId);
        window.open(response.data.signingUrl, "_blank");
        fetchDocument();
      }
    } catch (err) {
      alert("Failed to generate document.");
    } finally {
      setLoading(false);
    }
  };

return (
  <div className="p-6 mt-5">
    <div className="doc-table-container">
      <div className="doc-table-header">
        <div>Document Name</div>
        <div>Status</div>
        <div>Actions</div>
      </div>

      <div className="doc-row" style={{ padding: "5px" }}>
        {/* Document Name */}
        <div>
          <span style={{ fontWeight: 600 }}>
            {/* FIX 2: Show a placeholder until data is loaded to prevent flickering */}
            {!isDataLoaded ? "Loading regulations..." : documentTitle}
          </span>
        </div>

        {/* Status */}
        <div>
          {!isDataLoaded ? (
            <span className="status-pill status-pending">Checking...</span>
          ) : (
            <span className={`status-pill ${document?.status === "SIGNED" ? "status-signed" : "status-pending"}`}>
              {document?.status === "SIGNED" ? "Signed" : "Not signed"}
            </span>
          )}
        </div>

        {/* Actions: if signed show only download button; if not signed show only "Sign" button */}
        <div>
          {!isDataLoaded || loading ? (
            <span style={{ color: "#999" }}>{loading ? "Processing..." : "—"}</span>
          ) : document?.status === "SIGNED" ? (
            <a
              href={`${API_URL}/esign-documents/download/${(document?.legalesignDocId || "").replace(/^@+/, "").trim()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm"
              style={{
                background: "#D9A128",
                color: "#fff",
                border: "none",
                padding: "3px 10px",
                borderRadius: "4px",
                fontWeight: 600,
                fontSize: "12px",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <FontAwesomeIcon icon={faDownload} /> Download
            </a>
          ) : (
            <button
              type="button"
              className="btn btn-sm"
              onClick={handleSignOrDownload}
              disabled={loading}
              style={{
                background: "#D9A128",
                color: "#fff",
                border: "none",
                padding: "3px 10px",
                borderRadius: "4px",
                fontWeight: 600,
                fontSize: "12px",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
              }}
            >
              <FontAwesomeIcon icon={faFileSignature} /> Click here to sign
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
);
};

export default DocumentsPage;
